import React, { Dispatch, useRef, useEffect, useState } from "react";
import {
  AnchorDirectionShape,
  DateRangePicker,
  FocusedInputShape,
} from "react-dates";
import { DateRange } from "./HotelSearchForm";
import { FC } from "react";
import ClearDataButton from "./ClearDataButton";
import useWindowSize from "hooks/useWindowResize";
import moment, { Moment } from "moment";
import { CalendarIcon } from "@heroicons/react/outline";
import { AirportSearchAutocompleteResult, BestPricesModel } from "api/types";
import { b2cURL, getBestPricesUrl } from "api/helpers/api-constants";
import { Chip, Box, Typography, CircularProgress, Paper } from "@mui/material";
import { useReduxReducer } from "redux/store";
import { fetcherWithoutJSON } from "api/helpers/fetcher";
import { BsChevronRight } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import useSWR from "swr";

type Fields = "checkIn" | "checkOut";

export interface DatesRangeInputProps {
  defaultValue: DateRange;
  defaultFocus?: FocusedInputShape | null;
  onChange: Dispatch<React.SetStateAction<DateRange>>;
  onFocusChange?: (focus: FocusedInputShape | null) => void;
  startDateTime?: string | null;
  setStartDateTime?: React.Dispatch<React.SetStateAction<string | null>>;
  endDateTime?: string | null;
  setEndDateTime?: React.Dispatch<React.SetStateAction<string | null>>;
  minDate?: Moment | null;
  maxDate?: Moment | null;
  className?: string;
  fieldClassName?: string;
  wrapClassName?: string;
  numberOfMonths?: number;
  anchorDirection?: AnchorDirectionShape;
  checkinLabel?: string;
  checkoutLabel?: string;
  checkinDateError?: boolean;
  checkoutDateError?: boolean;
  IconCheckin?: React.ReactNode & FC<React.ComponentProps<"svg">>;
  IconCheckout?: React.ReactNode & FC<React.ComponentProps<"svg">>;
  value: DateRange;
  departureAirport?: AirportSearchAutocompleteResult | null;
  arrivalAirport?: AirportSearchAutocompleteResult | null;
  date: [moment.Moment | null, moment.Moment | null];
  setSelectedDate: (value: [moment.Moment | null, moment.Moment | null]) => void;
}

const DateInputFlight: FC<DatesRangeInputProps> = ({
  defaultValue,
  onChange,
  defaultFocus = null,
  onFocusChange,
  minDate,
  maxDate,
  className,
  fieldClassName = "[ nc-hero-field-padding ]",
  wrapClassName = "divide-y divide-neutral-200 dark:divide-neutral-700 lg:divide-y-0 md:border-l md:border-r border-neutral-200 lg:border-none",
  numberOfMonths,
  anchorDirection,
  checkinLabel = "",
  checkoutLabel = "",
  checkinDateError = false,
  checkoutDateError = false,
  IconCheckin = CalendarIcon,
  IconCheckout = CalendarIcon,
  value,
  departureAirport,
  arrivalAirport,
  date = [value.startDate, value.endDate],
  setSelectedDate,
}) => {
  const { t } = useTranslation();
  const [focusedInput, setFocusedInput] = useState(defaultFocus);
  const { isB2C } = useReduxReducer(state => state.general);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputRef2 = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMultiRoute, setIsMultiRoute] = useState(false);
  const { serviceFee } = useReduxReducer((state) => state.flight);
  const [bestPrices, setBestPrices] = useState<BestPricesModel[]>([]);
  const [directFlight, setDirectFlight] = useState<boolean>(false);
  const windowSize = useWindowSize();

  // State for route options and selection
  const [routeOptions, setRouteOptions] = useState<string[]>(
    departureAirport && arrivalAirport
      ? [`${departureAirport!.id.split(",")[0]}`, `${arrivalAirport!.id.split(",")[0]}`]
      : [""]
  );
  const [selectedRoute, setSelectedRoute] = useState<any>(routeOptions[0]);
  
  // State for date range calendar view
  const [dateRange, setDateRange] = useState<{
    startDate: Moment;
    endDate: Moment;
  }>({
    startDate: value.startDate ? moment(value.startDate).startOf("month") : moment(),
    endDate: value.endDate
      ? moment(value.endDate).add(1, "month").endOf("month")
      : moment().add(1, "month").endOf("month"),
  });
  
  // State for input date fields
  const [inputDate, setInputDate] = useState<string>(date[0] ? date[0]?.format("YYYY-MM-DD") : "");
  const [inputDateReturn, setInputDateReturn] = useState<string>(date[1] ? date[1]?.format("YYYY-MM-DD") : "");

  // Update route options when airports change
  useEffect(() => {
    setSelectedRoute(null);

    if (departureAirport?.id && arrivalAirport?.id) {
      const depAirports = departureAirport.id.split(",");
      const arrAirports = arrivalAirport.id.split(",");
      
      const options: any = [];
      
      depAirports.forEach(depAirport => {
        arrAirports.forEach(arrAirport => {
          options.push([depAirport, arrAirport]);
        });
      });
      
      setRouteOptions(options);
      setSelectedRoute(options[0]);
      setIsMultiRoute(options.length > 1);
    }
  }, [departureAirport, arrivalAirport]);

  // Update focused input when defaultFocus changes
  useEffect(() => {
    setFocusedInput(defaultFocus);
  }, [defaultFocus]);

  // Focus input field based on defaultFocus
  useEffect(() => {
    if (defaultFocus === "startDate") {
      inputRef.current?.focus();
    } else if (defaultFocus === "endDate") {
      inputRef2.current?.focus();
    } else {
      inputRef2.current?.blur();
    }
  }, [defaultFocus]);

  // Update input date fields when date prop changes
  useEffect(() => {
    if (date[0] && date[1]) {
      setInputDate(date[0]?.format("YYYY-MM-DD"));
      setInputDateReturn(date[1]?.format("YYYY-MM-DD"));
    }
  }, [date]);

  // Create a fetcher function for SWR
  const pricesFetcher = async (url: string) => {
    const response = await fetcherWithoutJSON(url);
    const data = await response.json();
    
    if (data.status === "error") {
      throw new Error("Failed to fetch prices");
    }
    
    return data;
  };
  
  // Generate a cache key based on route and date parameters
  const getPricesCacheKey = (from: string, to: string, startDate: string, endDate: string) => {
    return `${isB2C ? b2cURL : ''}${getBestPricesUrl(from, to, startDate, endDate)}`;
  };
  
  // Determine the current route based on focused input
  const getCurrentRoute = () => {
    if (!departureAirport?.id || !arrivalAirport?.id || !selectedRoute) return null;
    
    const originId = focusedInput === "startDate" ? selectedRoute[0] : selectedRoute[1];
    const destinationId = focusedInput === "startDate" ? selectedRoute[1] : selectedRoute[0];
    
    return { originId, destinationId };
  };
  
  // Get the current route
  const currentRoute = getCurrentRoute();
  
  // Create the cache key for SWR
  const cacheKey = currentRoute 
    ? getPricesCacheKey(
        currentRoute.originId, 
        currentRoute.destinationId, 
        moment().format("DD.MM.YYYY"), 
        moment().add(6, "month").format("DD.MM.YYYY")
      ) 
    : null;
  
  // Use SWR to fetch and cache the best prices data
  const { data: pricesData, error: pricesError, isValidating } = useSWR(
    cacheKey,
    pricesFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 3600000, // 1 hour
      onSuccess: (data) => {
        const newData = data.map((item: BestPricesModel) => ({
          ...item,
          isBestPrice: false,
        }));
        
        updateBestPricesData(newData);
      }
    }
  );
  
  // Update loading state based on SWR
  useEffect(() => {
    setIsLoading(isValidating);
  }, [isValidating]);
  
  // Update best prices when directFlight filter changes
  useEffect(() => {
    if (pricesData) {
      const newData = pricesData.map((item: BestPricesModel) => ({
        ...item,
        isBestPrice: false,
      }));
      
      updateBestPricesData(newData);
    }
  }, [directFlight, pricesData]);

  // Handle clearing date inputs
  const handleClearData = (field: Fields) => {
    switch (field) {
      case "checkIn":
        onChange((date: any) => ({ ...date, startDate: null }));
        setInputDate("");
        break;
      case "checkOut":
        onChange((date: any) => ({ ...date, endDate: null }));
        setInputDateReturn("");
        break;
      default:
        break;
    }
  };

  // Handle date focus change
  const handleDateFocusChange = (focus: FocusedInputShape | null) => {
    setFocusedInput(focus);
    onFocusChange && onFocusChange(focus);
  };

  // Update best prices data with highlighting
  const updateBestPrices = () => {
    let newData = bestPrices.map((item: BestPricesModel) => ({
      ...item,
      isBestPrice: false,
    }));

    let prices: { first: number[]; last: number[] } = { first: [], last: [] };

    if (directFlight) {
      newData = newData.filter((item: BestPricesModel) => item.nonStopPrice);
    }

    // Collect prices for first and second month
    newData.forEach((element: BestPricesModel) => {
      const price = directFlight ? element.nonStopPrice : element.bestPrice;
      
      if (moment(element.departureDate).isBetween(
        dateRange.startDate,
        moment(dateRange.startDate).add(1, "month").subtract(1, "day")
      ) && moment(element.departureDate).isAfter(moment()) && price) {
        prices.first.push(price);
      } else if (moment(element.departureDate).isBetween(
        moment(dateRange.endDate).subtract(1, "month").add(1, "day"),
        dateRange.endDate
      ) && moment(element.departureDate).isAfter(moment()) && price) {
        prices.last.push(price);
      }
    });

    prices.first.sort((a, b) => a - b);
    prices.last.sort((a, b) => a - b);

    // Mark best prices
    newData.forEach((element: BestPricesModel) => {
      const price = directFlight ? element.nonStopPrice : element.bestPrice;
      
      if (moment(element.departureDate).isBetween(
        dateRange.startDate,
        moment(dateRange.startDate).add(1, "month")
      ) && price === prices.first[0]) {
        element.isBestPrice = true;
      } else if (moment(element.departureDate).isBetween(
        moment(dateRange.endDate).subtract(1, "month"),
        dateRange.endDate
      ) && Math.ceil(price || 0) === Math.ceil(prices.last[0])) {
        element.isBestPrice = true;
      }
    });

    setBestPrices(newData);
  };

  // Helper function to update best prices data
  const updateBestPricesData = (newData: BestPricesModel[]) => {
    let prices: { first: number[]; last: number[] } = { first: [], last: [] };

    if (directFlight) {
      newData = newData.filter((item: BestPricesModel) => item.nonStopPrice);
    }

    // Collect prices for first and second month
    newData.forEach((element: BestPricesModel) => {
      const price = directFlight ? element.nonStopPrice : element.bestPrice;
      
      if (moment(element.departureDate).isBetween(
        dateRange.startDate,
        moment(dateRange.startDate).add(1, "month").subtract(1, "day")
      ) && moment(element.departureDate).isAfter(moment()) && price) {
        prices.first.push(price);
      } else if (moment(element.departureDate).isBetween(
        moment(dateRange.endDate).subtract(1, "month").add(1, "day"),
        dateRange.endDate
      ) && moment(element.departureDate).isAfter(moment()) && price) {
        prices.last.push(price);
      }
    });

    prices.first.sort((a, b) => a - b);
    prices.last.sort((a, b) => a - b);

    // Mark best prices
    newData.forEach((element: BestPricesModel) => {
      const price = directFlight ? element.nonStopPrice : element.bestPrice;
      
      if (moment(element.departureDate).isBetween(
        dateRange.startDate,
        moment(dateRange.startDate).add(1, "month")
      ) && price === prices.first[0]) {
        element.isBestPrice = true;
      } else if (moment(element.departureDate).isBetween(
        moment(dateRange.endDate).subtract(1, "month"),
        dateRange.endDate
      ) && Math.ceil(price || 0) === Math.ceil(prices.last[0])) {
        element.isBestPrice = true;
      }
    });

    setBestPrices(newData);
  };

  // Render day content in calendar
  const renderDayContent = (day: moment.Moment) => {
    const priceData = bestPrices.find(
      (item) => moment(item.departureDate).format("YYYY-MM-DD") === day.format("YYYY-MM-DD")
    );

    if (priceData) {
      const isSelected = day.format("DD.MM.YYYY") === value.startDate?.format("DD.MM.YYYY") || 
                         day.format("DD.MM.YYYY") === value?.endDate?.format("DD.MM.YYYY");
      const isPast = moment(day).isBefore(minDate);
      const price = directFlight ? priceData.nonStopPrice : priceData.bestPrice;
      const showPrice = directFlight ? !!priceData.nonStopPrice : true;
      
      return (
        <Box className="flex flex-col items-center">
          <Typography 
            variant="body2"
            className={`${
              isSelected ? "!text-white" : isPast ? "!text-gray-300" : "!text-black"
            }`}
          >
            {day.format("D")}
          </Typography>
          {showPrice && (
            <Typography 
              variant="caption"
              className={`
                ${isSelected ? "!text-gray-100" : isPast ? "!text-gray-300" : "!text-gray-700"}
                ${priceData.isBestPrice ? "font-semibold !text-[#ff690f]" : ""}
              `}
            >
              {price ? Math.ceil(price) + serviceFee : "--"} €
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Typography 
        variant="body2"
        className={`${
          day.format("DD.MM.YYYY") === value.startDate?.format("DD.MM.YYYY") || 
          day.format("DD.MM.YYYY") === value?.endDate?.format("DD.MM.YYYY")
            ? "!text-white"
            : moment(day).isBefore(minDate)
              ? "!text-gray-300"
              : "!text-black"
        }`}
      >
        {day.format("D")}
      </Typography>
    );
  };

  // Handle departure date input change
  const handleDepartureDateChange = (input: string) => {
    setInputDate(input);
    
    const newStartDate = input.length > 0 ? moment(input) : date[0];
    if (newStartDate && newStartDate.isValid()) {
      setSelectedDate([newStartDate, date[1]]);
    }
  };

  // Handle return date input change
  const handleReturnDateChange = (input: string) => {
    setInputDateReturn(input);
    
    const newReturnDate = input.length > 0 ? moment(input) : date[1];
    if (newReturnDate && newReturnDate.isValid()) {
      setSelectedDate([date[0], newReturnDate]);
    }
  };

  // Render departure date input
  const renderDepartureDateInput = () => {
    const focused = focusedInput === "startDate";
    const errorClassName = checkinDateError ? "text-red-400" : "";
    
    return (
      <Box
        className={`relative flex flex-1 ${fieldClassName} h-full flex-shrink-0 cursor-pointer items-center space-x-3 transition-colors duration-200 ${
          focused ? "bg-blue-50 dark:bg-blue-900/20" : "hover:bg-blue-50 dark:hover:bg-blue-900/10"
        } rounded-xl`}
        onClick={() => handleDateFocusChange("startDate")}
      >
        <Box className="flex items-center">
          <IconCheckin className="h-5 w-5 text-neutral-500" />
        </Box>
        
        <Box className="flex-grow">
          <input
            type="date"
            ref={inputRef}
            className={`block w-full font-semibold border-none bg-transparent ${errorClassName} hide-calendar-icon`}
            value={inputDate}
            onChange={(e) => setInputDate(e.target.value)}
            onBlur={() => handleDepartureDateChange(inputDate)}
            placeholder={checkinLabel || t("app.add-date")}
          />
          {focused && <ClearDataButton onClick={() => handleClearData("checkIn")} />}
        </Box>
      </Box>
    );
  };

  // Render return date input
  const renderReturnDateInput = () => {
    const focused = focusedInput === "endDate";
    const errorClassName = checkoutDateError ? "text-red-400" : "";
    
    return (
      <Box
        className={`relative flex flex-1 ${fieldClassName} h-full flex-shrink-0 cursor-pointer items-center space-x-3 transition-colors duration-200 ${
          focused ? "bg-blue-50 dark:bg-blue-900/20" : "hover:bg-blue-50 dark:hover:bg-blue-900/10"
        } rounded-xl`}
        onClick={() => handleDateFocusChange("endDate")}
      >
        <Box className="flex items-center">
          <IconCheckout className="h-5 w-5 text-neutral-500" />
        </Box>
        
        <Box className="flex-grow">
          <input
            type="date"
            ref={inputRef2}
            className={`block w-full font-semibold border-none bg-transparent ${errorClassName} hide-calendar-icon`}
            value={inputDateReturn}
            onChange={(e) => setInputDateReturn(e.target.value)}
            onBlur={() => handleReturnDateChange(inputDateReturn)}
            placeholder={checkoutLabel || t("app.add-date")}
          />
          {focused && <ClearDataButton onClick={() => handleClearData("checkOut")} />}
        </Box>
      </Box>
    );
  };

  // Render calendar info (route selection)
  const renderCalendarInfo = () => {
    if (!departureAirport?.id || !arrivalAirport?.id) return null;
    
    const fromCode = focusedInput === "startDate" ? departureAirport.id : arrivalAirport.id;
    const toCode = focusedInput === "startDate" ? arrivalAirport.id : departureAirport.id;
    
    return (
      <Box className="p-3 border-b">
        <Box className="flex items-center justify-center mb-2">
          <Typography variant="subtitle2" className="font-medium">
            {fromCode} <BsChevronRight className="inline mx-1" /> {toCode}
            {isValidating && <CircularProgress size={16} className="ml-2" />}
          </Typography>
        </Box>
        
        {isMultiRoute && (
          <Box className="flex flex-wrap justify-center mt-2">
            {routeOptions.map((option, index) => {
              const routeLabel = focusedInput === "startDate" 
                ? `${option[0]} > ${option[1]}`
                : `${option[1]} > ${option[0]}`;
                
              return (
                <Chip
                  key={index}
                  label={routeLabel}
                  size="small"
                  variant={selectedRoute === option ? "filled" : "outlined"}
                  color={selectedRoute === option ? "primary" : "default"}
                  onClick={() => setSelectedRoute(option)}
                  className="m-1"
                />
              );
            })}
          </Box>
        )}
        
        <Box className="flex justify-between mt-3">
        
          
          {pricesError && (
            <Typography variant="caption" className="text-red-500">
              {t("flight.search.error-loading-prices")}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box className="[ lg:nc-flex-2 ] relative z-10 w-full">
      <Box className="absolute inset-x-0 bottom-0">
        <DateRangePicker
          keepFocusOnInput
          startDate={moment(date[0])}
          endDate={moment(date[1])}
          focusedInput={focusedInput}
          onDatesChange={(date) => {
            onChange(date);
            setSelectedDate([moment(date.startDate), moment(date.endDate)]);
          }}
          onFocusChange={handleDateFocusChange}
          numberOfMonths={numberOfMonths || (windowSize.width <= 1024 ? 1 : undefined)}
          startDateId="nc-hero-stay-startDateId"
          endDateId="nc-hero-stay-endDateId"
          daySize={windowSize.width > 500 ? 56 : undefined}
          orientation="horizontal"
          showClearDates
          noBorder
          hideKeyboardShortcutsPanel
          anchorDirection={anchorDirection}
          firstDayOfWeek={1}
          renderDayContents={(day) =>
            isLoading ? (
              <Box className="flex flex-col items-center">
                <Typography variant="body2" className={`
                  ${day.format("DD.MM.YYYY") === (focusedInput === "startDate" 
                    ? value.startDate?.format("DD.MM.YYYY") 
                    : value?.endDate?.format("DD.MM.YYYY"))
                    ? "!text-white"
                    : moment(day).isBefore(minDate)
                      ? "!text-gray-200"
                      : "!text-black"
                  }`}
                >
                  {day.format("D")}
                </Typography>
                <Box className="h-2 w-6 animate-pulse rounded-full bg-primary-300"></Box>
              </Box>
            ) : (
              renderDayContent(day)
            )
          }
          calendarInfoPosition="top"
          renderCalendarInfo={renderCalendarInfo}
          customArrowIcon={<span className="mx-2">→</span>}
        />
      </Box>

      <Paper 
        elevation={0} 
        className={`relative flex h-full w-full flex-shrink-0 flex-col lg:flex-row lg:items-center ${wrapClassName}`}
      >
        {renderDepartureDateInput()}
        {renderReturnDateInput()}
      </Paper>
      
      <style jsx global>{`
        .hide-calendar-icon::-webkit-calendar-picker-indicator {
          display: none;
        }
        .DateRangePicker_picker {
          z-index: 20;
        }
        .CalendarDay__selected {
          background: #3b82f6 !important;
        }
        .CalendarDay__selected_span {
          background: #bfdbfe !important;
          color: #1e40af !important;
        }
        .CalendarDay__hovered_span {
          background: #dbeafe !important;
          color: #1e40af !important;
        }
      `}</style>
    </Box>
  );
};

export default DateInputFlight;
